import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from './cn';

/**
 * Модальне вікно.
 *
 * Замінює саморобні `fixed inset-0`: Radix бере на себе те, що завжди роблять
 * неправильно — пастку фокуса, повернення фокуса на елемент, який відкрив
 * вікно, `aria-modal`, блокування прокрутки під вікном і Escape.
 *
 * `variant="drawer"` — та сама механіка, але панель виїжджає збоку: на
 * телефоні фільтри показують саме так.
 *
 * `variant="sheet"` — панель праворуч на всю висоту з власною розміткою, як
 * `bare`: шапку й низ малює вміст. Підкладка світліша — сторінка під панеллю
 * лишається читною, бо оператор звіряє з нею те, що робить.
 */
export function Dialog({
	open,
	onOpenChange,
	title,
	description,
	variant = 'modal',
	className,
	children,
	footer,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Обовʼязковий для доступності; сховати візуально можна через `srOnlyTitle`. */
	title: string;
	description?: string;
	variant?: 'modal' | 'drawer' | 'bare' | 'sheet';
	className?: string;
	children: ReactNode;
	footer?: ReactNode;
}): ReactNode {
	return (
		<RadixDialog.Root open={open} onOpenChange={onOpenChange}>
			<RadixDialog.Portal>
				{/* Підкладка навмисно темна в обох темах: під нею вміст сторінки. */}
				<RadixDialog.Overlay
					className={cn(
						'fixed inset-0 z-50 data-[state=open]:animate-in data-[state=open]:fade-in-0',
						variant === 'sheet' ? 'bg-black/20' : 'bg-black/60 backdrop-blur-[2px]',
					)}
				/>
				<RadixDialog.Content
					className={cn(
						'fixed z-50 flex flex-col bg-surface text-fg shadow-e2 focus:outline-none',
						variant === 'drawer'
							? 'inset-y-0 left-0 w-[min(22rem,88vw)] border-r border-border'
							: variant === 'sheet'
								? 'inset-y-0 right-0 w-[min(28.5rem,100vw)] border-l border-border data-[state=open]:animate-in data-[state=open]:slide-in-from-right-4'
								: variant === 'bare'
									? 'inset-0'
									: 'left-1/2 top-1/2 max-h-[85vh] w-[min(32rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-card border border-border',
						className,
					)}
				>
					{variant === 'bare' || variant === 'sheet' ? (
						<>
							<RadixDialog.Title className="sr-only">{title}</RadixDialog.Title>
							{description && <RadixDialog.Description className="sr-only">{description}</RadixDialog.Description>}
							{children}
						</>
					) : (
						<>
							<header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
								<div>
									<RadixDialog.Title className="text-sm font-semibold">{title}</RadixDialog.Title>
									{description && (
										<RadixDialog.Description className="mt-0.5 text-xs text-fg-muted">
											{description}
										</RadixDialog.Description>
									)}
								</div>
								<RadixDialog.Close
									aria-label="Close"
									className="grid size-7 shrink-0 place-items-center rounded-md text-fg-subtle transition-colors hover:bg-surface-muted hover:text-fg"
								>
									<X className="size-4" />
								</RadixDialog.Close>
							</header>

							<div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>

							{footer && <footer className="border-t border-border px-4 py-3">{footer}</footer>}
						</>
					)}
				</RadixDialog.Content>
			</RadixDialog.Portal>
		</RadixDialog.Root>
	);
}

export const DialogClose = RadixDialog.Close;
